import axios from 'axios';
import nprogress from 'nprogress';

import history from '@/navigation/history';
import { enumToValues } from '@/types/enums';
import { AuthTokenErrorCodeEnum, ErrorDomainEnum } from '@/types/error';
import resolveURLSlash from '@/utils/resolveURLSlash';

interface XMLHttpRequestError extends Error {
  status: number;
}

/**
 * HTTP Client 인스턴스 생성
 */
const fileV2 = axios.create({
  baseURL: resolveURLSlash(import.meta.env.VITE_FILE_API_HOST, 'v2'),
});

/**
 * 프로그레스바 설정
 */
nprogress.configure({ showSpinner: false, speed: 500 });

/**
 * 📬 요청 인터셉터 설정
 */
fileV2.interceptors.request.use(
  (config) => {
    // 요청 헤더 객체가 존재하지 않으면 빈 객체로 초기화
    if (!config.headers) config.headers = {};
    // 파일 서버 인증키 설정
    config.headers[
      'ACCESS_TOKEN'
    ] = `y9fxKLiN6vncou7kgYncrhKJPYfFPKk3nZ9zlXXwiOI=`;
    // 리소스를 받아올 때 발생하는 다운로드 진행 이벤트 설정
    config.onDownloadProgress = (e) => {
      const percentage = Math.floor(e.loaded * 1.0) / e.total;
      nprogress.set(percentage);
    };
    // 리소스를 보낼 때 발생하는 업로드 진행 이벤트 설정
    config.onUploadProgress = (e) => {
      const percentage = Math.floor(e.loaded * 1.0) / e.total;
      nprogress.set(percentage);
    };
    return config;
  },
  (error) => Promise.reject(error.request)
);

/**
 * 📦 응답 인터셉터 설정
 */
fileV2.interceptors.response.use(
  (response) => {
    nprogress.done();
    return response;
  },
  (error) => {
    nprogress.done();

    if (axios.isAxiosError(error)) {
      if (error.response) {
        // 🌈 요청이 발생했고, 응답이 발생한 경우
        const status = error.response.status;

        const errorDomain =
          error.response.headers?.['error_domain']?.toUpperCase();
        const errorCode = error.response.headers?.['error_code']?.toUpperCase();

        // 서버에서 에러를 핸들링하여 응답으로 내려주는 경우
        if (errorDomain && errorCode) {
          if (isDev) {
            console.log(JSON.stringify({ errorDomain, errorCode }, null, 2));
          }

          // 🚨 서버에서 핸들링 했으나 500번대 에러인 경우,
          // 🚨 서버에서 핸들링을 하였고 500번대 에러가 아니나, 그에 따른 정책 미비로 인해 클라이언트에서 처리할 수 없는 에러
          if (status >= 500 || errorDomain === ErrorDomainEnum.FALLBACK) {
            history.replace(`/error?code=${status}`);
            return Promise.resolve();
          }

          if (errorDomain === ErrorDomainEnum.AUTH) {
            const isAuthTokenError = enumToValues(
              AuthTokenErrorCodeEnum
            ).includes(errorCode);

            // 🔑 인증 토큰 관련 에러일 경우에만 로그아웃 실행
            if (isAuthTokenError) {
              history.replace(`/logout?code=${errorCode}`);
              return Promise.resolve();
            }
          }
          // 🌈 각 도메인으로 에러 도메인 및 코드 전달
          return Promise.reject({ errorDomain, errorCode });
        } else {
          // 🚨 서버에서 fallback으로도 처리하지 못한 에러=
          history.replace(
            `/error?code=${(error as unknown as XMLHttpRequestError).status}`
          );
          return Promise.resolve();
        }
      } else {
        // 🚨 요청이 발생했으나, 서버 응답이 발생하지 않은 경우 또는 요청 과정에서 알 수 없는 이유로 에러를 발생 시킨 경우
        console.error(error);
        // history.push(
        //   `/error?code=${(error as unknown as XMLHttpRequestError).status}`
        // );
        return Promise.resolve();
      }
    }
    // 🚨 HTTP Client가 처리하지 못하는 에러
    console.error(error);
    history.push(
      `/error?code=${(error as unknown as XMLHttpRequestError).status}`
    );
    return Promise.resolve();
  }
);

export default fileV2;
